// src/components/principal/WebsiteApiKey.jsx (v20 – expose apiKey via onApiKeyReady)

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/api";
import { API_PATHS } from "../../config";

const WebsiteApiKey = ({
  // CMS snapshot fields from /profile
  cmsPlatformCode,
  cmsPlatformLabel,
  cmsBuilderCode,
  cmsBuilderLabel,
  cmsLastDetectedAt,

  schoolId,
  principalName,
  onError,
  schoolWebsite = "",
  onSchoolWebsiteUpdated,

  onProfileRefresh, // expected to return mapped profile (PrincipalDashboard.refreshProfile does)

  // ✅ NEW: allow parent (wizard/dashboard) to receive the apiKey
  onApiKeyReady,
}) => {
  const [apiKey, setApiKey] = useState(null);
  const [status, setStatus] = useState("");
  const [loadingKey, setLoadingKey] = useState(false);

  const [websiteInput, setWebsiteInput] = useState(schoolWebsite || "");
  const [savingWebsite, setSavingWebsite] = useState(false);
  const [websiteSavedMsg, setWebsiteSavedMsg] = useState("");

  const [rerunningAnalysis, setRerunningAnalysis] = useState(false);

  // ✅ UI feedback for polling
  const [isPolling, setIsPolling] = useState(false);

  // ---- Polling control (safe: one in-flight, no intervals) ----
  const pollTimeoutRef = useRef(null);
  const pollStopRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const pollActiveRef = useRef(false);

  const stopPolling = useCallback(() => {
    pollActiveRef.current = false;
    setIsPolling(false);

    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (pollStopRef.current) {
      clearTimeout(pollStopRef.current);
      pollStopRef.current = null;
    }
    pollInFlightRef.current = false;
  }, []);

  useEffect(() => {
    return () => stopPolling(); // cleanup on unmount
  }, [stopPolling]);

  useEffect(() => {
    setWebsiteInput(schoolWebsite || "");
  }, [schoolWebsite]);

  const stripTrailingSlashes = useCallback(
    (s) => (s || "").replace(/\/+$/, ""),
    []
  );

  const normalizeWebsiteUrl = useCallback((raw) => {
    const s = (raw || "").trim();
    if (!s) return "";
    if (!/^https?:\/\//i.test(s)) return `https://${s}`;
    return s;
  }, []);

  const isValidWebsiteUrl = useCallback(
    (raw) => {
      const s = (raw || "").trim();
      if (!s) return true; // allow blank in UI
      try {
        const u = new URL(normalizeWebsiteUrl(s));
        return u.protocol === "http:" || u.protocol === "https:";
      } catch {
        return false;
      }
    },
    [normalizeWebsiteUrl]
  );

  const websiteIsValid = useMemo(
    () => isValidWebsiteUrl(websiteInput),
    [websiteInput, isValidWebsiteUrl]
  );

  const websiteNormalized = useMemo(
    () => stripTrailingSlashes(normalizeWebsiteUrl(websiteInput)),
    [websiteInput, normalizeWebsiteUrl, stripTrailingSlashes]
  );

  const websiteHasValue = useMemo(
    () => (schoolWebsite || "").trim().length > 0,
    [schoolWebsite]
  );

  // Snapshot presence
  const hasSnapshot = useMemo(
    () => Boolean(cmsPlatformCode) || Boolean(cmsLastDetectedAt),
    [cmsPlatformCode, cmsLastDetectedAt]
  );

  // "Pending" means: they saved a website, but snapshot hasn't come back yet
  const analysisPending = useMemo(
    () => websiteHasValue && !hasSnapshot,
    [websiteHasValue, hasSnapshot]
  );

  const cmsSummary = useMemo(() => {
    if (!cmsPlatformLabel && !cmsBuilderLabel) return null;
    const parts = [];
    if (cmsPlatformLabel) parts.push(cmsPlatformLabel);
    if (cmsBuilderLabel) parts.push(cmsBuilderLabel);
    return parts.join(" + ");
  }, [cmsPlatformLabel, cmsBuilderLabel]);

  const isWordPress = useMemo(() => {
    if (!cmsPlatformCode) return false;
    const code = String(cmsPlatformCode).toUpperCase();
    return code === "WORDPRESS" || code === "WORDPRESSCOM";
  }, [cmsPlatformCode]);

  // ✅ Keep parent informed when WP is not detected (clear key)
  useEffect(() => {
    if (!isWordPress) {
      onApiKeyReady?.(null);
    }
  }, [isWordPress, onApiKeyReady]);

  // If snapshot arrives while we were polling, stop the spinner/polling immediately.
  useEffect(() => {
    if (hasSnapshot && pollActiveRef.current) {
      stopPolling();
    }
  }, [hasSnapshot, stopPolling]);

  // ---- Poll helper: every 5–8s for up to 90s; stop once snapshot appears ----
  const startPollingProfile = useCallback(async () => {
    if (typeof onProfileRefresh !== "function") return;
    // ✅ NEW: if already polling, don't start another loop
    if (pollActiveRef.current) return;
    // restart cleanly (guarantees no duplicates)
    stopPolling();
    pollActiveRef.current = true;
    setIsPolling(true);

    const pickDelayMs = () => 5000 + Math.floor(Math.random() * 3000); // 5000–8000ms

    const hasSnapshotFromMapped = (mapped) => {
      if (!mapped) return false;
      return Boolean(mapped.cmsPlatformCode) || Boolean(mapped.cmsLastDetectedAt);
    };

    const tick = async () => {
      if (!pollActiveRef.current) return;
      if (pollInFlightRef.current) return; // never overlap

      pollInFlightRef.current = true;
      try {
        const mapped = await onProfileRefresh();

        // ✅ stop early as soon as we have the snapshot back
        if (hasSnapshotFromMapped(mapped)) {
          stopPolling();
          return;
        }
      } catch {
        // ignore transient errors; keep trying until timeout
      } finally {
        pollInFlightRef.current = false;
      }

      if (!pollActiveRef.current) return;
      pollTimeoutRef.current = setTimeout(tick, pickDelayMs());
    };

    // fire immediately once
    tick();

    // hard stop after 90s
    pollStopRef.current = setTimeout(() => {
      pollActiveRef.current = false;
      setIsPolling(false);

      try {
        stopPolling();
      } finally {
        setStatus("Website check timed out. Click Re-check to try again.");
      }
    }, 90000);
  }, [onProfileRefresh, stopPolling]);

  const handleRerunAnalysis = async () => {
    if (!websiteHasValue) {
      onError?.("Please save your school website first.");
      return;
    }
    if (!API_PATHS.RERUN_SCHOOL_WEBSITE_ANALYSIS) {
      onError?.("Missing API_PATHS.RERUN_SCHOOL_WEBSITE_ANALYSIS in config.js.");
      return;
    }

    setRerunningAnalysis(true);
    setStatus("");

    try {
      await api.post(API_PATHS.RERUN_SCHOOL_WEBSITE_ANALYSIS, {});
      setStatus("Re-check started.");
      setTimeout(() => setStatus(""), 2500);

      await startPollingProfile();
    } catch (e) {
      console.error("Re-run analysis failed:", e);
      const msg =
        e?.response?.data?.detail ||
        "Could not start website re-check. Please try again.";
      onError?.(msg);
      stopPolling();
    } finally {
      setRerunningAnalysis(false);
    }
  };

  const handleSaveWebsite = async () => {
    if (!websiteIsValid) {
      onError?.("Please enter a valid website URL (e.g. https://yourschool.ie).");
      return;
    }
    if (!API_PATHS.UPDATE_SCHOOL_WEBSITE) {
      onError?.("Missing API_PATHS.UPDATE_SCHOOL_WEBSITE in config.js.");
      return;
    }

    setSavingWebsite(true);
    setWebsiteSavedMsg("");
    setStatus("");

    try {
      const { data } = await api.patch(API_PATHS.UPDATE_SCHOOL_WEBSITE, {
        school_website: websiteNormalized || "",
      });

      const savedUrl = data?.school_website ?? (websiteNormalized || "");
      onSchoolWebsiteUpdated?.(savedUrl);

      const scheduled = Boolean(data?.analysis_scheduled);
      setWebsiteSavedMsg(scheduled ? "Saved. Re-checking website…" : "Saved.");
      setTimeout(() => setWebsiteSavedMsg(""), 3000);

      // ✅ if backend scheduled analysis (or if we’re pending anyway), start polling
      if (scheduled || analysisPending) {
        await startPollingProfile();
      } else if (typeof onProfileRefresh === "function") {
        // still refresh once to keep UI in sync
        try {
          await onProfileRefresh();
        } catch {
          // ignore
        }
      }
    } catch (e) {
      console.error("Failed to update school website:", e);
      const msg =
        e?.response?.data?.detail ||
        "Could not save your school website. Please try again.";
      onError?.(msg);
      stopPolling();
    } finally {
      setSavingWebsite(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setStatus("Copied to clipboard!");
        setTimeout(() => setStatus(""), 3000);
      })
      .catch(() => setStatus("Failed to copy."));
  };

  const copyPrefilledEmail = () => {
    const body = `Dear Website Administrator,

Our school is now using the 'GhrianGhraffy App' to enhance how we share school photos with parents.

Please update our school website by installing the "GhrianGhraffy API Photo Display" plugin from the main WordPress plugin directory (search for "GhrianGhraffy").

Once installed and activated:

1. Go to Settings → General in WordPress.
2. Scroll to the "GhrianGhraffy" section.
3. Paste the API Key below into the "GhrianGhraffy API Key" field.
4. Click "Connect to GhrianGhraffy".
5. Confirm the correct school name and School ID appear.
6. Save changes at the bottom.

API Key: ${apiKey}

(If needed, our School ID is: ${schoolId})

This will create a secure "Photos" page on the website.

Kind regards,
${principalName || "School Principal"}`;

    copyText(body);
  };

  useEffect(() => {
    if (!isWordPress) {
      setApiKey(null);
      setLoadingKey(false);
      setStatus("");

      // ✅ also tell parent that no key is available
      onApiKeyReady?.(null);
      return;
    }

    const fetchOrGenerateKey = async () => {
      setLoadingKey(true);
      try {
        const { data } = await api.get(API_PATHS.GET_API_KEY);
        setApiKey(data.api_key);
        onApiKeyReady?.(data.api_key); // ✅ NEW
        setStatus("");
      } catch (e) {
        if (e.response?.status === 404) {
          try {
            const { data } = await api.post(API_PATHS.GENERATE_API_KEY, {});
            setApiKey(data.api_key);
            onApiKeyReady?.(data.api_key); // ✅ NEW
            setStatus("API key created successfully!");
          } catch (gerr) {
            console.error("Generate API key failed:", gerr);
            setStatus("Failed to generate API key. Please try again.");
            onError?.("Failed to generate API key.");
            onApiKeyReady?.(null); // ✅ NEW
          }
        } else {
          console.error("Fetch API key failed:", e);
          setStatus("Failed to load API key. Please refresh.");
          onError?.("Failed to load API key.");
          onApiKeyReady?.(null); // ✅ NEW
        }
      } finally {
        setLoadingKey(false);
      }
    };

    fetchOrGenerateKey();
  }, [isWordPress, onError, onApiKeyReady]);

  const showChecking = rerunningAnalysis || isPolling;

  return (
    <div className="principal-panel website-panel">
      <div className="principal-card">
        <h3 className="panel-title" style={{ marginBottom: 6 }}>
          GhrianGhraffy and Your School&apos;s current Website:
        </h3>
        <p className="api-key-helper-text" style={{ marginTop: 0 }}>
          When photos are taken using the GhrianGhraffy App on your phone, we can send them to
          your school&apos;s current website.
          Below is the website we&apos;ve detected for your School. If it&apos;s incorrect, you can change it
          and then hit &apos;save&apos;.
        </p>

        {showChecking && (
          <div className="helper-status-message" aria-live="polite" style={{ marginTop: 10 }}>
            <span
              aria-hidden="true"
              style={{
                width: 12,
                height: 12,
                borderRadius: 999,
                border: "2px solid rgba(226, 232, 240, 0.45)",
                borderTopColor: "rgba(0, 255, 175, 0.95)",
                display: "inline-block",
                animation: "gg-spin 900ms linear infinite",
              }}
            />
            Checking your website…
          </div>
        )}
        {!showChecking && analysisPending && (
          <div className="helper-status-message" aria-live="polite" style={{ marginTop: 10 }}>
            Website saved — analysis is queued. This can take up to ~90 seconds.
            If it doesn’t update, click <strong>Re-check</strong>.
          </div>
        )}        

        {cmsSummary && (
          <p className="api-key-helper-text" style={{ marginTop: 10 }}>
            Detected: <strong>{cmsSummary}</strong>
            {cmsLastDetectedAt ? (
              <span> (last checked: {new Date(cmsLastDetectedAt).toLocaleDateString()})</span>
            ) : null}
          </p>
        )}

        <div className="principal-form-row">
          <input
            id="schoolWebsite"
            className="principal-input"
            type="url"
            inputMode="url"
            placeholder="https://yourschool.ie"
            value={websiteInput}
            onChange={(e) => setWebsiteInput(e.target.value)}
          />
          {!websiteIsValid && (
            <div className="principal-field-hint error">
              Please enter a valid URL (example: https://yourschool.ie)
            </div>
          )}
        </div>

        {/* Actions row: Save + Re-run analysis */}
        <div
          className="principal-actions-row"
          style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}
        >
          <button
            type="button"
            className="btn btn-primary website-copy-button"
            onClick={handleSaveWebsite}
            disabled={savingWebsite || !websiteIsValid}
          >
            {savingWebsite ? "Saving…" : "Save"}
          </button>

          <button
            type="button"
            className="btn"
            onClick={handleRerunAnalysis}
            disabled={rerunningAnalysis || !websiteHasValue}
            title="Force a fresh website analysis"
          >
            {rerunningAnalysis ? "Re-checking…" : "Re-check"}
          </button>

          {websiteSavedMsg && (
            <span className="helper-status-message" aria-live="polite">
              {websiteSavedMsg}
            </span>
          )}
        </div>

        {status && (
          <p className="helper-status-message" aria-live="polite" style={{ marginTop: 10 }}>
            {status}
          </p>
        )}
      </div>

      {/* Status cards below */}
      {analysisPending && (
        <div className="principal-card" style={{ marginTop: 14 }}>
          <h3 className="panel-title">Plugin setup</h3>
          <p className="api-key-helper-text">
            We’ll enable the plugin section automatically once WordPress is confirmed.
          </p>
        </div>
      )}

      {!analysisPending && !isWordPress && (
        <div className="principal-card" style={{ marginTop: 14 }}>
          <p className="no-wordpress-message">
            Unfortunately it appears Your School&apos;s website is not built with WordPress, so
            please{" "}
            <a href="/contact" className="template-download-link">
              Contact us
            </a>{" "}
            and we will update your site.
          </p>
        </div>
      )}

      {!analysisPending && isWordPress && (
        <>
          {loadingKey && (
            <div className="principal-card" style={{ marginTop: 14 }}>
              <p>Loading API key…</p>
            </div>
          )}

          {!loadingKey && !apiKey && (
            <div className="principal-card" style={{ marginTop: 14 }}>
              <h3 className="panel-title">Plugin API Access</h3>
              <p>Unable to load or generate API key. Please try again later.</p>
            </div>
          )}

          {!loadingKey && apiKey && (
            <>
              <p style={{ marginTop: 14 }}>
                To Enable sending photos to your School&apos;s website, send an email to your website administrator
                requesting them to add the <strong>GhrianGhraffy Wordpress Plugin</strong> to your website:
                (use the suggested text below)
              </p>

              <textarea
                className="api-email-textarea"
                value={`Dear Website Administrator,

Our school is now using the 'GhrianGhraffy App' to enhance how we share school photos with parents.

Please update our school website (it takes less than 5 minutes) by installing the "GhrianGhraffy API Photo Display"
plugin from the main WordPress plugin directory (search for "GhrianGhraffy").

Once installed and activated:

1. Go to Settings → General in WordPress.
2. Scroll to the "GhrianGhraffy" section.
3. Paste the API Key below into the "GhrianGhraffy API Key" field.
4. Click "Connect to GhrianGhraffy".
5. Confirm the correct school name and School ID appear.
6. Save changes at the bottom.

API Key: ${apiKey}

(If needed, our School ID is: ${schoolId})

This will create a secure "Photos" page on the website.

Please let me know when it's done.

Kind regards,
${principalName || "School Principal"}`}
                readOnly
                onClick={(e) => e.target.select()}
                rows={20}
              />

              <button
                type="button"
                onClick={copyPrefilledEmail}
                className="btn btn-primary website-copy-button"
              >
                Copy Email to Clipboard
              </button>
            </>
          )}
        </>
      )}

      {/* Tiny keyframes for the inline spinner */}
      <style>{`
        @keyframes gg-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default WebsiteApiKey;
